import { useEffect, useState } from 'react'
import { Tooltip } from 'react-tooltip'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ApiCaller } from '@frontend/api'
import type { Content } from '@types'
import { Modal } from '@ui/components'
import { useSentry } from '@utils/frontend'

interface SourceModalProps {
	readonly content: Content;
	readonly isOpen: boolean;
	readonly summary?: string | undefined;
	readonly title?: string | undefined;
	readonly onClose: () => void;
}

export default function SourceModal({ content, isOpen, summary, title, onClose }: SourceModalProps) {
	const modalTitle = (
		<div className="flex items-center space-x-4">
			<div className="flex flex-col">
				<p className="text-lg font-bold">
					{ content.title }
				</p>
				<p className="text-sm text-gray-500">
					Please find below the exact document extracts that have been used by your VirtualBrain to generate this answer.
				</p>
			</div>

		</div>
	)

	return (
		<Modal
			title={ modalTitle }
			isOpen={ isOpen }
			size="xlarge"
			onClose={ onClose }
		>
			<div className="flex flex-col space-y-4">
				{ summary
					? <>
						<div className="flex items-center justify-between font-bold text-sm text-primary">
							<div className="flex items-center">
								<FontAwesomeIcon icon="star" className="mr-2" />
							Summary - Automatically generated by VirtualBrain
							</div>
							<SourceActions content={ content } />
						</div>
						<div className="h-auto p-4 bg-blue-100 rounded-lg text-xs leading-normal whitespace-pre-line">
							<p className="font-bold mb-2 text-sm">
								{ title }
							</p>
							{ summary.split('.').reduce<string[]>((acc, sentence, index) => {
								if (index % 2 === 0) {
									acc.push(sentence + '.')
								} else {
									acc[acc.length - 1] += sentence + '.\n'
								}

								return acc
							}, []).join('\n').replace(/\.\s*$/, '').replace(/\n$/, '') }
						</div>
					</>
					: null }
				{
					content.vectors?.map((vector, index) => {
						const formattedText = vector.text.replace(/\b\d+\b/g, '<strong>$&</strong>')

						return (
							<div key={ vector.id }>
								<div className="flex justify-between items-center font-bold text-sm pb-3 text-primary">
									<div className="flex items-center">
										<FontAwesomeIcon icon="quote-left" className="mr-2" />
										{ `Extract #${index + 1}` }
									</div>
									{ index === 0 && !summary && (
										<SourceActions content={ content } />
									) }
								</div>
								<div className="h-auto p-4 bg-gray-100 rounded-lg text-xs leading-normal whitespace-pre-line">
									{ /* eslint-disable-next-line @typescript-eslint/naming-convention, react/no-danger */ }
									<p dangerouslySetInnerHTML={ { __html: formattedText } } />
								</div>
							</div>
						)
					})
				}

			</div>
		</Modal>
	)
}

function SourceActions({ content }: { readonly content: Content }) {
	const [parentFolderUrl, setParentFolderUrl] = useState<string>()
	const { captureException } = useSentry()

	useEffect(() => {
		async function loadContentFolder() {
			if (content.source === 'upload') {
				return
			}

			try {
				const url = await ApiCaller.getContentParentFolderUrl(content.id)

				setParentFolderUrl(url)
			} catch (error) {
				captureException(error)
			}
		}

		loadContentFolder()
	}, [content])

	return (
		<div className="flex space-x-2">
			<a
				href={ content.url }
				target="_blank"
				rel="noreferrer"
				className={ `flex items-center justify-center py-2 px-3 text-sm
					font-medium rounded-md transition-colors duration-200 ease-in-out
					bg-blue-50 hover:bg-blue-100 text-blue-600 border border-blue-200` }>
				<FontAwesomeIcon icon="external-link-alt" className="mr-2" />
				Open original document
			</a>
			<button
				type="button"
				data-tooltip-id="folder-tooltip"
				className={ `
						flex items-center justify-center py-2 px-3
						text-sm font-medium rounded-md transition-colors duration-200 ease-in-out
						${parentFolderUrl
			? 'bg-gray-50 hover:bg-gray-100 text-gray-600 hover:text-gray-800 border border-gray-200'
			: 'bg-gray-200 text-gray-400 border border-gray-200'}
					` }
				disabled={ !parentFolderUrl }
				onClick={ () => {
					if (parentFolderUrl) {
						window.open(parentFolderUrl, '_blank', 'noopener,noreferrer')
					}
				} }>
				<FontAwesomeIcon icon="folder-open" className="mr-2" />
				Open parent folder
			</button>
			{ parentFolderUrl
				? null
				: <Tooltip id="folder-tooltip" className="z-10 !opacity-100 max-w-sm">
					If the file was uploaded using drag and drop,
					we are unable to open its containing folder
				</Tooltip> }
		</div>
	)
}
